import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  FolderOpen, 
  Users, 
  Clock, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  Calendar,
  BarChart3,
  Target,
  AlertTriangle,
  Plus
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useApp } from '../contexts/AppContext';
import { calculateCapacitesEquipe } from '../utils/capacityUtils';
import OnboardingModal from '../components/OnboardingModal';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter, 
  startOfYear, 
  endOfYear,
  differenceInDays,
  parseISO,
  format,
  addMonths,
  isWithinInterval
} from 'date-fns';
import { fr } from 'date-fns/locale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { state, actions } = useApp();

  console.log(state);

  // Gestion de l'onboarding
  React.useEffect(() => {
    // Vérifier si l'onboarding est nécessaire au chargement du composant
    if (state.auth.isAuthenticated && !state.onboarding.hasCompletedOnboarding) {
      const hasCompleted = actions.checkOnboardingStatus();
      if (!hasCompleted && !state.onboarding.showOnboarding) {
        // Ne pas afficher automatiquement l'onboarding à chaque chargement
        // Il sera affiché seulement après inscription via useAuthSubmission
      }
    }
  }, [state.auth.isAuthenticated, state.onboarding.hasCompletedOnboarding, state.onboarding.showOnboarding, actions]);

  // Récupérer les dates de période de manière stable
  const periodDates = React.useMemo(() => {
    const periodSelection = state.periodSelection;
    const now = new Date();
    
    if (periodSelection.type === 'preset') {
      switch (periodSelection.preset) {
        case 'current_month':
          return { start: startOfMonth(now), end: endOfMonth(now) };
        case 'current_quarter':
          return { start: startOfQuarter(now), end: endOfQuarter(now) };
        case 'fiscal_year': {
          // Exercice en cours : du 1er juillet dernier au 30 juin prochain
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth(); // 0-11
          
          // Si nous sommes avant le 1er juillet (mois 6), l'exercice a commencé l'année dernière
          if (currentMonth < 6) {
            const start = new Date(currentYear - 1, 6, 1); // 1er juillet année dernière
            const end = new Date(currentYear, 5, 30); // 30 juin année courante
            return { start, end };
          } else {
            // Si nous sommes le 1er juillet ou après, l'exercice a commencé cette année
            const start = new Date(currentYear, 6, 1); // 1er juillet année courante
            const end = new Date(currentYear + 1, 5, 30); // 30 juin année prochaine
            return { start, end };
          }
        }
        case '6_months':
          return { start: startOfMonth(now), end: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 5, now.getDate())) };
        default:
          return { start: startOfMonth(now), end: endOfMonth(now) };
      }
    } else if (periodSelection.type === 'custom') {
      return { 
        start: periodSelection.startDate ? new Date(periodSelection.startDate) : startOfMonth(now), 
        end: periodSelection.endDate ? new Date(periodSelection.endDate) : endOfMonth(now) 
      };
    } else if (periodSelection.type === 'single') {
      const date = periodSelection.singleDate ? new Date(periodSelection.singleDate) : now;
      return { start: date, end: date };
    }
    
    return { start: startOfMonth(now), end: endOfMonth(now) };
  }, [state.periodSelection]);

  // Charger les temps planifiés pour la période sélectionnée
  React.useEffect(() => {
    if (state.auth.isAuthenticated && periodDates) {
      const { start, end } = periodDates;
      actions.loadAllTempsPlanifies({
        date_debut: format(start, 'yyyy-MM-dd'),
        date_fin: format(end, 'yyyy-MM-dd')
      });
    }
  }, [state.auth.isAuthenticated, periodDates, actions]);

  // Recharger les projets et personnes quand la période change
  React.useEffect(() => {
    if (state.auth.isAuthenticated && periodDates) {
      // Recharger les projets pour récupérer les données actualisées
      actions.loadProjets();
      // Les personnes peuvent aussi être rechargées car les calculs de capacité dépendent de la période
      actions.loadPersonnes();
    }
  }, [state.auth.isAuthenticated, periodDates, actions]);

  // Calcul des statistiques
  const stats = {
    totalProjets: state.projets?.data?.length,
    projetsActifs: state.projets?.data?.filter(p => p.statut === 'en_cours').length,
    totalPersonnes: state.personnes?.data?.filter(p => p.actif).length,
    totalBudgetJours: state.projets?.data?.reduce((sum, p) => sum + (p.budget_total || 0), 0)
  };

  // Calculs pour temps facturable/non facturable depuis les données agrégées de l'API
  const tempsStats = useMemo(() => {
    const projets = state.projets?.data || [];
    
    const totalFacturable = projets.reduce((sum, p) => sum + (p.consomme_facturable || 0), 0);
    const totalNonFacturable = projets.reduce((sum, p) => sum + (p.consomme_non_facturable || 0), 0);
    const total = totalFacturable + totalNonFacturable;

    return {
      totalFacturable,
      totalNonFacturable,
      total,
      pourcentageFacturable: total > 0 ? (totalFacturable / total) * 100 : 0
    };
  }, [state.projets?.data]);

  // Calcul de la capacité nette totale de l'équipe
  const capaciteStats = useMemo(() => {
    const personnes = state.personnes?.data || [];
    const { start, end } = periodDates;
    const capacitesEquipe = calculateCapacitesEquipe(personnes, start, end);
    
    return {
      totalCapaciteNette: capacitesEquipe.totalCapaciteNette,
      nombrePersonnes: capacitesEquipe.nombrePersonnes
    };
  }, [state.personnes?.data, periodDates]);

  // Données pour le graphique par période
  const periodData = useMemo(() => {
    const periods = [];
    const periodCapaciteNette = [];
    const periodBudgetPlanifie = [];
    const periodBudgetConsomme = [];

    const { start, end } = periodDates;
    const daysDiff = differenceInDays(end, start);
    const personnes = state.personnes?.data || [];
    const projets = state.projets?.data || [];
    
    // Déterminer la granularité selon la durée
    let currentPeriods = [];
    if (daysDiff <= 180) { // <= 6 mois : données mensuelles
      let current = startOfMonth(start);
      while (current <= end) {
        const periodEnd = endOfMonth(current);
        currentPeriods.push({
          start: current,
          end: periodEnd > end ? end : periodEnd,
          label: format(current, 'MMM', { locale: fr })
        });
        current = addMonths(current, 1);
      }
    } else if (daysDiff <= 730) { // <= 2 ans : données trimestrielles
      let current = startOfQuarter(start);
      while (current <= end) {
        const periodEnd = endOfQuarter(current);
        currentPeriods.push({
          start: current,
          end: periodEnd > end ? end : periodEnd,
          label: `T${Math.floor(current.getMonth() / 3) + 1} ${current.getFullYear()}`
        });
        current = addMonths(current, 3);
      }
    } else { // > 2 ans : données annuelles
      let current = startOfYear(start);
      while (current <= end) {
        const periodEnd = endOfYear(current);
        currentPeriods.push({
          start: current,
          end: periodEnd > end ? end : periodEnd,
          label: current.getFullYear().toString()
        });
        current = addMonths(current, 12);
      }
    }

    // Calculer les données pour chaque période
    currentPeriods.forEach(period => {
      periods.push(period.label);

      // Capacité nette de l'équipe pour cette période
      const capacitesEquipe = calculateCapacitesEquipe(personnes, period.start, period.end);
      periodCapaciteNette.push(capacitesEquipe.totalCapaciteNette || 0);

      // Budget planifié des projets actifs dans cette période
      const budgetPlanifie = projets
        .filter(p => {
          const projetStart = parseISO(p.date_debut);
          const projetEnd = parseISO(p.date_fin);
          return isWithinInterval(projetStart, { start: period.start, end: period.end }) ||
                 isWithinInterval(projetEnd, { start: period.start, end: period.end }) ||
                 (projetStart <= period.start && projetEnd >= period.end);
        })
        .reduce((sum, p) => sum + (p.budget_total || 0), 0);
      periodBudgetPlanifie.push(budgetPlanifie);

      // Budget consommé des projets dans cette période
      const budgetConsomme = projets
        .filter(p => {
          const projetStart = parseISO(p.date_debut);
          const projetEnd = parseISO(p.date_fin);
          return isWithinInterval(projetStart, { start: period.start, end: period.end }) ||
                 isWithinInterval(projetEnd, { start: period.start, end: period.end }) ||
                 (projetStart <= period.start && projetEnd >= period.end);
        })
        .reduce((sum, p) => sum + (p.consomme_total || 0), 0);
      periodBudgetConsomme.push(budgetConsomme);
    });

    return { periods, periodCapaciteNette, periodBudgetPlanifie, periodBudgetConsomme };
  }, [periodDates, state.personnes?.data, state.projets?.data]);

  // Données pour le graphique par projet (utilise les données agrégées de l'API)
  const projetData = useMemo(() => {
    const projetsActifs = state.projets?.data?.filter(p => p.statut === 'en_cours').slice(0, 5) || [];
    const projetNoms = projetsActifs.map(p => p.nom.length > 15 ? p.nom.substring(0, 15) + '...' : p.nom);
    const projetPlanifie = projetsActifs.map(p => p.budget_total || 0);
    const projetConsomme = projetsActifs.map(p => p.consomme_total || 0);
    const projetConsommeFacturable = projetsActifs.map(p => p.consomme_facturable || 0);
    const projetConsommeNonFacturable = projetsActifs.map(p => p.consomme_non_facturable || 0);

    return { projetNoms, projetPlanifie, projetConsomme, projetConsommeFacturable, projetConsommeNonFacturable };
  }, [state.projets?.data]);

  // Données pour le GANTT chart
  const ganttData = useMemo(() => {
    const projets = state.projets?.data || [];
    
    // Utiliser les dates de période du contexte
    const { start: periodStart, end: periodEnd } = actions.getPeriodDates();

    // Filtrer les projets qui se chevauchent avec la période
    const projetsFiltered = projets.filter(p => {
      const projetStart = parseISO(p.date_debut);
      const projetEnd = parseISO(p.date_fin);
      
      return isWithinInterval(projetStart, { start: periodStart, end: periodEnd }) ||
             isWithinInterval(projetEnd, { start: periodStart, end: periodEnd }) ||
             (projetStart <= periodStart && projetEnd >= periodEnd);
    });

    const totalDays = differenceInDays(periodEnd, periodStart);

    const projetsGantt = projetsFiltered.map(projet => {
      const projetStart = parseISO(projet.date_debut);
      const projetEnd = parseISO(projet.date_fin);
      
      // Calculer la position et la largeur de la barre
      const startFromPeriod = Math.max(0, differenceInDays(projetStart, periodStart));
      const endFromPeriod = Math.min(totalDays, differenceInDays(projetEnd, periodStart));
      const duration = endFromPeriod - startFromPeriod;
      
      const leftPercent = (startFromPeriod / totalDays) * 100;
      const widthPercent = (duration / totalDays) * 100;

      // Calculer le pourcentage de réalisation
      const budgetTotal = projet.budget_total || 0;
      const consommeTotal = projet.consomme_total || 0;
      const progressPercent = budgetTotal > 0 ? Math.min(100, (consommeTotal / budgetTotal) * 100) : 0;

      return {
        ...projet,
        leftPercent: Math.max(0, leftPercent),
        widthPercent: Math.max(0, widthPercent),
        progressPercent,
        formattedStart: format(projetStart, 'dd/MM/yyyy', { locale: fr }),
        formattedEnd: format(projetEnd, 'dd/MM/yyyy', { locale: fr }),
        duration: differenceInDays(projetEnd, projetStart) + 1
      };
    });

    return {
      projets: projetsGantt,
      periodStart,
      periodEnd,
      totalDays
    };
  }, [state.projets?.data, actions]);

  // Données pour le graphique des temps planifiés par semaine
  const tempsPlanifiesData = useMemo(() => {
    const tempsPlanifies = state.tempsPlanifies?.data || [];
    
    if (tempsPlanifies.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Créer un Map pour agréger par semaine
    const weeklyData = new Map();
    
    tempsPlanifies.forEach(temps => {
      const semaine = temps.semaine;
      if (!weeklyData.has(semaine)) {
        weeklyData.set(semaine, {
          semaine,
          analyse_design: 0,
          build: 0,
          test: 0,
          total: 0
        });
      }
      
      const week = weeklyData.get(semaine);
      week.analyse_design += parseFloat(temps.analyse_design || 0);
      week.build += parseFloat(temps.build || 0);
      week.test += parseFloat(temps.test || 0);
      week.total = week.analyse_design + week.build + week.test;
    });

    // Convertir en array et trier par semaine
    const sortedWeeks = Array.from(weeklyData.values()).sort((a, b) => 
      new Date(a.semaine).getTime() - new Date(b.semaine).getTime()
    );

    // Préparer les labels (format simplifié)
    const labels = sortedWeeks.map(week => {
      const date = parseISO(week.semaine);
      return format(date, 'dd/MM', { locale: fr });
    });

    // Préparer les datasets
    const datasets = [
      {
        label: 'Analyse & Design',
        data: sortedWeeks.map(week => week.analyse_design),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Développement',
        data: sortedWeeks.map(week => week.build),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Tests',
        data: sortedWeeks.map(week => week.test),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Total planifié',
        data: sortedWeeks.map(week => week.total),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.05)',
        borderDash: [5, 5],
        tension: 0.1,
        fill: false,
        borderWidth: 2
      }
    ];

    return { labels, datasets, weeklyData: sortedWeeks };
  }, [state.tempsPlanifies?.data]);

  // Composant GanttChart
  const GanttChart = ({ data }) => {
    const getStatusColor = (statut) => {
      switch (statut) {
        case 'planifie': return 'bg-blue-500';
        case 'en_cours': return 'bg-green-500';
        case 'termine': return 'bg-gray-500';
        case 'suspendu': return 'bg-red-500';
        default: return 'bg-gray-400';
      }
    };

    const getStatusText = (statut) => {
      switch (statut) {
        case 'planifie': return 'Planifié';
        case 'en_cours': return 'En cours';
        case 'termine': return 'Terminé';
        case 'suspendu': return 'Suspendu';
        default: return statut;
      }
    };

    // Calculer la position de la date d'aujourd'hui
    const getTodayPosition = () => {
      const today = new Date();
      const dayFromStart = differenceInDays(today, data.periodStart);
      
      // Vérifier si aujourd'hui est dans la période visible
      if (dayFromStart >= 0 && dayFromStart <= data.totalDays) {
        return (dayFromStart / data.totalDays) * 100;
      }
      return null;
    };

    const todayPosition = getTodayPosition();

    // Générer les marqueurs temporels
    const generateTimeMarkers = () => {
      const markers = [];
      const start = data.periodStart;
      const end = data.periodEnd;
      const daysDiff = differenceInDays(end, start);
      
      // Choisir la granularité selon la durée
      if (daysDiff <= 180) { // <= 6 mois : marqueurs mensuels
        let current = startOfMonth(start);
        while (current <= end) {
          const position = (differenceInDays(current, start) / data.totalDays) * 100;
          markers.push({
            position,
            label: format(current, 'MMM', { locale: fr }),
            date: current
          });
          current = addMonths(current, 1);
        }
      } else if (daysDiff <= 730) { // <= 2 ans : marqueurs trimestriels
        let current = startOfQuarter(start);
        while (current <= end) {
          const position = (differenceInDays(current, start) / data.totalDays) * 100;
          markers.push({
            position,
            label: `T${Math.floor(current.getMonth() / 3) + 1} ${current.getFullYear()}`,
            date: current
          });
          current = addMonths(current, 3);
        }
      } else { // > 2 ans : marqueurs annuels
        let current = startOfYear(start);
        while (current <= end) {
          const position = (differenceInDays(current, start) / data.totalDays) * 100;
          markers.push({
            position,
            label: current.getFullYear().toString(),
            date: current
          });
          current = addMonths(current, 12);
        }
      }
      
      return markers;
    };

    const timeMarkers = generateTimeMarkers();

    return (
      <div className="space-y-4">
        {/* Timeline header */}
        <div className="relative h-8 bg-gray-50 rounded border">
          {timeMarkers.map((marker, index) => (
            <div
              key={index}
              className="absolute top-0 h-full flex items-center"
              style={{ left: `${marker.position}%` }}
            >
              <div className="w-px h-full bg-gray-300"></div>
              <span className="text-xs text-gray-600 ml-2 font-medium">
                {marker.label}
              </span>
            </div>
          ))}
          
          {/* Ligne d'aujourd'hui */}
          {todayPosition !== null && (
            <div
              className="absolute top-0 h-full flex items-center pointer-events-none z-10"
              style={{ left: `${todayPosition}%` }}
            >
              <div className="w-0.5 h-full bg-red-500"></div>
              <span className="text-xs text-red-600 ml-2 font-medium bg-white px-1 rounded shadow-sm">
                Aujourd'hui
              </span>
            </div>
          )}
        </div>

        {/* Projects */}
        <div className="space-y-3 relative">
          {/* Ligne d'aujourd'hui qui traverse tous les projets */}
          {todayPosition !== null && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-red-500 opacity-50 pointer-events-none z-10"
              style={{ left: `${todayPosition}%` }}
            ></div>
          )}
          
          {data.projets.map((projet) => (
            <div key={projet.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h4 className="font-medium text-gray-900 text-sm truncate max-w-xs">
                    {projet.nom}
                  </h4>
                  <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(projet.statut)}`}>
                    {getStatusText(projet.statut)}
                  </span>
                  {!projet.facturable && (
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                      Non facturable
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {projet.formattedStart} - {projet.formattedEnd} ({projet.duration}j)
                </div>
              </div>
              
              <div className="relative h-6 bg-gray-100 rounded">
                {/* Barre de projet */}
                <div
                  className={`absolute top-0 h-full rounded ${getStatusColor(projet.statut)} opacity-80`}
                  style={{
                    left: `${projet.leftPercent}%`,
                    width: `${projet.widthPercent}%`
                  }}
                >
                  {/* Barre de progression */}
                  <div
                    className="h-full bg-white bg-opacity-40 rounded"
                    style={{ width: `${projet.progressPercent}%` }}
                  ></div>
                </div>
                
                {/* Tooltip info */}
                <div className="absolute inset-0 flex items-center px-2 text-xs text-white font-medium truncate">
                  {projet.widthPercent > 15 && (
                    <span>
                      {projet.progressPercent.toFixed(0)}% ({projet.consomme_total || 0}j/{projet.budget_total || 0}j)
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {data.projets.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucun projet trouvé pour la période sélectionnée
          </div>
        )}
      </div>
    );
  };

  const chartDataPeriod = {
    labels: periodData.periods,
    datasets: [
      {
        label: 'Capacité nette équipe',
        data: periodData.periodCapaciteNette,
        backgroundColor: 'rgba(147, 51, 234, 0.5)',
        borderColor: 'rgb(147, 51, 234)',
        borderWidth: 1,
      },
      {
        label: 'Budget planifié',
        data: periodData.periodBudgetPlanifie,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
      {
        label: 'Budget consommé',
        data: periodData.periodBudgetConsomme,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
      },
    ],
  };

  const chartDataProjet = {
    labels: projetData.projetNoms,
    datasets: [
      {
        label: 'Budget planifié',
        data: projetData.projetPlanifie,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
      {
        label: 'Budget consommé',
        data: projetData.projetConsomme,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const datasetIndex = context.datasetIndex;
            const dataIndex = context.dataIndex;
            
            if (datasetIndex === 1) { // Budget planifié
              const capacite = periodData.periodCapaciteNette[dataIndex] || 0;
              const budget = context.parsed.y;
              if (capacite > 0) {
                const ratio = (budget / capacite * 100).toFixed(1);
                return `Charge: ${ratio}% de la capacité`;
              }
            } else if (datasetIndex === 2) { // Budget consommé
              const budget = periodData.periodBudgetPlanifie[dataIndex] || 0;
              const consomme = context.parsed.y;
              if (budget > 0) {
                const ratio = (consomme / budget * 100).toFixed(1);
                return `Réalisation: ${ratio}% du budget`;
              }
            }
            return null;
          }
        }
      }
    },
          scales: {
        x: {
          title: {
            display: true,
            text: 'Période'
          }
        },
      y: {
        title: {
          display: true,
          text: 'Jours'
        },
        beginAtZero: true,
      },
    },
  };

  const tempsPlanifiesChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Temps planifiés par semaine',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} jours`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Semaines'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Jours planifiés'
        },
        beginAtZero: true,
        stacked: false
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  return (
    <>
      {/* Modal d'onboarding */}
      <OnboardingModal 
        isOpen={state.onboarding.showOnboarding}
        onClose={actions.completeOnboarding}
      />
      
      <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="mt-2 text-sm text-gray-700">
          Vue d'ensemble de vos projets et de l'activité de l'équipe
        </p>
      </div>

      {/* Statistiques générales */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FolderOpen className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5">
              {stats.totalProjets === 0 ? (
                <>
                  <p className="text-sm font-medium text-gray-500 truncate">Projets</p>
                  <Link 
                    to="/projets"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors mt-1"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Créer un projet
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-500 truncate">Projets actifs</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.projetsActifs}/{stats.totalProjets}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5">
              {stats.totalPersonnes === 0 ? (
                <>
                  <p className="text-sm font-medium text-gray-500 truncate">Équipe</p>
                  <Link 
                    to="/personnes"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors mt-1"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une personne
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-500 truncate">Équipe active</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalPersonnes}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500 truncate">Temps facturable</p>
              <p className="text-2xl font-semibold text-gray-900">{tempsStats?.totalFacturable?.toFixed(1)}j</p>
              <p className="text-xs text-gray-500">{tempsStats?.pourcentageFacturable?.toFixed(1)}% du total</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Target className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500 truncate">Jours planifiés</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalBudgetJours}j</p>
              <p className="text-xs text-gray-500">Budget total des projets</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500 truncate">Capacité nette</p>
              <p className="text-2xl font-semibold text-gray-900">{capaciteStats?.totalCapaciteNette?.toFixed(1)}j</p>
              <p className="text-xs text-gray-500">Équipe ({capaciteStats?.nombrePersonnes} pers.)</p>
            </div>
          </div>
        </div>
      </div>


            {/* Planning GANTT */}
            <div className="mb-8">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Planning des projets</h3>
            </div>
            <div className="text-sm text-gray-600">
              Période contrôlée depuis la barre de navigation
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center space-x-6 text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>Planifié</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>En cours</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gray-500 rounded"></div>
                <span>Terminé</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span>Suspendu</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-white bg-opacity-40 border rounded"></div>
                <span>Progression (temps consommé)</span>
              </div>
            </div>
          </div>
          
          <GanttChart data={ganttData} />
        </div>
      </div>

      {/* Temps planifiés par semaine */}
      <div className="mb-8">
        <div className="card w-full">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-6 w-6 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Charge planifiée par semaine</h3>
            </div>
            <div className="text-sm text-gray-600">
              Répartition des temps planifiés sur la période
            </div>
          </div>
          
          {tempsPlanifiesData.labels.length > 0 ? (
            <div className="w-full" style={{ height: '400px' }}>
              <Line data={tempsPlanifiesData} options={tempsPlanifiesChartOptions} />
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun temps planifié trouvé pour la période sélectionnée</p>
              <p className="text-sm mt-2">Les temps planifiés apparaîtront automatiquement lors de la création des projets avec phases</p>
            </div>
          )}
        </div>
      </div>

      {/* Graphiques analytiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Graphique par période */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Évolution temporelle</h3>
            <div className="text-sm text-gray-500">
              Capacité vs Budget (jours)
            </div>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={chartDataPeriod} options={chartOptions} />
          </div>
        </div>

        {/* Graphique par projet */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Projets actifs</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={chartDataProjet} options={{
              ...chartOptions,
              scales: {
                ...chartOptions.scales,
                x: {
                  ...chartOptions.scales.x,
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45
                  }
                }
              }
            }} />
          </div>
        </div>
      </div>



      {/* Actions rapides et alertes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
          <div className="space-y-3">
            <Link
              to="/projets"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <FolderOpen className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-sm font-medium">Voir tous les projets</span>
            </Link>
            <Link
              to="/personnes"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Users className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-sm font-medium">Gérer l'équipe</span>
            </Link>
            <Link
              to="/saisie-temps"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Clock className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-sm font-medium">Saisir du temps</span>
            </Link>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Alertes et notifications</h3>
          <div className="space-y-3">
            {tempsStats.pourcentageFacturable < 70 && (
              <div className="flex items-start p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Temps facturable faible</p>
                  <p className="text-xs text-yellow-700">
                    Seulement {tempsStats?.pourcentageFacturable?.toFixed(1)}% du temps est facturable
                  </p>
                </div>
              </div>
            )}
            
            {stats.projetsActifs === 0 && (
              <div className="flex items-start p-3 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Aucun projet actif</p>
                  <p className="text-xs text-red-700">
                    Créez ou activez des projets pour commencer
                  </p>
                </div>
              </div>
            )}

            {tempsStats.pourcentageFacturable >= 80 && (
              <div className="flex items-start p-3 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Excellent taux de facturation</p>
                  <p className="text-xs text-green-700">
                    {tempsStats?.pourcentageFacturable?.toFixed(1)}% du temps est facturable
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Dashboard; 